import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Package,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Check,
  X,
  Clock,
  Building,
  FileCheck,
  Shield,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import { getUserErrorMessage } from '../../utils/errorMessages';

const CATEGORIES = [
  { value: 'compliance', label: 'Compliance' },
  { value: 'security', label: 'Security' },
  { value: 'general', label: 'Algemeen' }
];

const AdminModulesPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [requests, setRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('modules');
  const [searchQuery, setSearchQuery] = useState('');
  const [allRequests, setAllRequests] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_monthly: 0,
    features: '',
    category: 'general',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [modulesRes, requestsRes, allReqRes] = await Promise.all([
        api.get('/modules'),
        api.get('/modules/requests/pending').catch(() => ({ data: [] })),
        api.get('/modules/requests/all').catch(() => ({ data: [] })),
      ]);
      setModules(modulesRes.data || []);
      setRequests(requestsRes.data || []);
      setAllRequests(allReqRes.data || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
      toast.error('Data laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveModule = async () => {
    if (!formData.name || !formData.description) {
      toast.error('Vul alle verplichte velden in');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        price_monthly: parseFloat(formData.price_monthly) || 0,
        features: formData.features.split('\n').filter(f => f.trim())
      };

      if (editingModule) {
        await api.patch(`/modules/${editingModule.id}`, payload);
        toast.success('Module bijgewerkt');
      } else {
        await api.post('/modules', payload);
        toast.success('Module aangemaakt');
      }
      
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Opslaan mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Weet u zeker dat u deze module wilt verwijderen?')) return;
    
    try {
      await api.delete(`/modules/${moduleId}`);
      toast.success('Module verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await api.post(`/modules/requests/${requestId}/approve`);
      toast.success('Aanvraag goedgekeurd');
      fetchData();
    } catch (error) {
      toast.error('Goedkeuren mislukt');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await api.post(`/modules/requests/${requestId}/reject`);
      toast.success('Aanvraag afgewezen');
      fetchData();
    } catch (error) {
      toast.error('Afwijzen mislukt');
    }
  };

  const openEditModal = (module) => {
    setEditingModule(module);
    setFormData({
      name: module.name,
      description: module.description,
      price_monthly: module.price_monthly,
      features: module.features?.join('\n') || '',
      category: module.category,
      is_active: module.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingModule(null);
    setFormData({
      name: '',
      description: '',
      price_monthly: 0,
      features: '',
      category: 'general',
      is_active: true
    });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'compliance':
        return FileCheck;
      case 'security':
        return Shield;
      default:
        return Package;
    }
  };

  const filteredModules = modules.filter(m => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs text-neutral-500 mb-1">ADMIN</p>
            <h1 className="text-2xl font-bold tracking-tight text-black dark:text-white">
              Module Beheer
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              Beheer premium modules en aanvragen
            </p>
          </div>
          <Button 
            className="bg-black text-white hover:bg-neutral-800"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            data-testid="add-module-button"
          >
            <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
            Nieuwe Module
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-neutral-200 dark:border-neutral-800 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Package className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black dark:text-white">{modules.length}</p>
                  <p className="text-xs text-neutral-500">Totaal modules</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-neutral-200 dark:border-neutral-800 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Check className="h-5 w-5 text-emerald-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black dark:text-white">
                    {modules.filter(m => m.is_active).length}
                  </p>
                  <p className="text-xs text-neutral-500">Actieve modules</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-neutral-200 dark:border-neutral-800 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black dark:text-white">{requests.length}</p>
                  <p className="text-xs text-neutral-500">Openstaande aanvragen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-neutral-200 dark:border-neutral-800 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Building className="h-5 w-5 text-purple-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black dark:text-white">
                    {modules.reduce((sum, m) => sum + (m.is_subscribed ? 1 : 0), 0)}
                  </p>
                  <p className="text-xs text-neutral-500">Actieve subscriptions</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-neutral-100 dark:bg-neutral-800 p-1 rounded-full">
            <TabsTrigger 
              value="modules" 
              className="rounded-full px-6 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-neutral-700 data-[state=active]:shadow-sm"
            >
              Modules ({modules.length})
            </TabsTrigger>
            <TabsTrigger 
              value="requests" 
              className="rounded-full px-6 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-neutral-700 data-[state=active]:shadow-sm"
            >
              Openstaand ({requests.length})
            </TabsTrigger>
            <TabsTrigger 
              value="history" 
              className="rounded-full px-6 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-neutral-700 data-[state=active]:shadow-sm"
              data-testid="admin-tab-history"
            >
              Geschiedenis ({allRequests.filter(r => r.status !== 'pending').length})
            </TabsTrigger>
          </TabsList>

          {/* Modules Tab */}
          <TabsContent value="modules" className="mt-6">
            {/* Search */}
            <div className="mb-4">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                <Input
                  placeholder="Zoek modules..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-modules-input"
                />
              </div>
            </div>

            {/* Modules Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredModules.map((module) => {
                const Icon = getCategoryIcon(module.category);
                return (
                  <Card 
                    key={module.id}
                    className={`border-neutral-200 dark:border-neutral-800 shadow-sm ${
                      !module.is_active ? 'opacity-60' : ''
                    }`}
                    data-testid={`module-card-${module.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-lg ${
                            module.category === 'compliance' ? 'bg-blue-100' :
                            module.category === 'security' ? 'bg-red-100' : 'bg-neutral-100'
                          } flex items-center justify-center`}>
                            <Icon className={`h-5 w-5 ${
                              module.category === 'compliance' ? 'text-blue-600' :
                              module.category === 'security' ? 'text-red-600' : 'text-neutral-600'
                            }`} strokeWidth={1.5} />
                          </div>
                          <div>
                            <CardTitle className="text-base font-semibold text-black dark:text-white">
                              {module.name}
                            </CardTitle>
                            <Badge className={module.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-500'}>
                              {module.is_active ? 'Actief' : 'Inactief'}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditModal(module)}
                            data-testid={`edit-module-${module.id}`}
                          >
                            <Edit className="h-4 w-4" strokeWidth={1.5} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteModule(module.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-module-${module.id}`}
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3 line-clamp-2">
                        {module.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-black dark:text-white">
                          €{module.price_monthly}/maand
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {module.features?.length || 0} features
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests" className="mt-6">
            {requests.length === 0 ? (
              <Card className="border-neutral-200 dark:border-neutral-800 shadow-sm">
                <CardContent className="p-8 text-center">
                  <Clock className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                  <h3 className="text-lg font-medium text-black dark:text-white mb-2">Geen openstaande aanvragen</h3>
                  <p className="text-sm text-neutral-500">Module aanvragen van klanten verschijnen hier</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {requests.map((request) => (
                  <Card key={request.id} className="border-neutral-200 dark:border-neutral-800 shadow-sm" data-testid={`pending-request-${request.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                            <Package className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-black dark:text-white">{request.module_name}</h4>
                            <p className="text-sm text-neutral-500">
                              {request.organization_name} • {request.requested_by_email}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-neutral-500 mr-4">
                            {new Date(request.created_at).toLocaleDateString('nl-NL')}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleRejectRequest(request.id)}
                            data-testid={`reject-request-${request.id}`}
                          >
                            <X className="h-4 w-4 mr-1" strokeWidth={1.5} />
                            Afwijzen
                          </Button>
                          <Button
                            size="sm"
                            className="bg-emerald-600 text-white hover:bg-emerald-700"
                            onClick={() => handleApproveRequest(request.id)}
                            data-testid={`approve-request-${request.id}`}
                          >
                            <Check className="h-4 w-4 mr-1" strokeWidth={1.5} />
                            Goedkeuren
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-6" data-testid="history-tab-content">
            {(() => {
              const historyRequests = allRequests.filter(r => r.status !== 'pending');
              if (historyRequests.length === 0) {
                return (
                  <Card className="border-neutral-200 dark:border-neutral-800 shadow-sm">
                    <CardContent className="p-8 text-center">
                      <FileCheck className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                      <h3 className="text-lg font-medium text-black dark:text-white mb-2">Geen geschiedenis</h3>
                      <p className="text-sm text-neutral-500">Verwerkte aanvragen verschijnen hier zodra er aanvragen zijn beoordeeld.</p>
                    </CardContent>
                  </Card>
                );
              }
              return (
                <div className="space-y-3">
                  {historyRequests.map((request) => {
                    const isApproved = request.status === 'approved';
                    return (
                      <Card 
                        key={request.id} 
                        className={`border shadow-sm ${
                          isApproved 
                            ? 'border-emerald-200 dark:border-emerald-800' 
                            : 'border-red-200 dark:border-red-800'
                        }`}
                        data-testid={`history-request-${request.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                                isApproved 
                                  ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                                  : 'bg-red-100 dark:bg-red-900/30'
                              }`}>
                                {isApproved 
                                  ? <Check className="h-5 w-5 text-emerald-600" strokeWidth={1.5} />
                                  : <X className="h-5 w-5 text-red-600" strokeWidth={1.5} />
                                }
                              </div>
                              <div>
                                <h4 className="font-semibold text-black dark:text-white">{request.module_name}</h4>
                                <p className="text-sm text-neutral-500">
                                  {request.organization_name} • {request.requested_by_email}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <Badge className={`text-[10px] ${
                                  isApproved
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                }`}>
                                  {isApproved ? 'Goedgekeurd' : 'Afgewezen'}
                                </Badge>
                                <div className="text-[10px] text-neutral-400 mt-1">
                                  Aangevraagd: {new Date(request.created_at).toLocaleDateString('nl-NL')}
                                </div>
                                {request.reviewed_at && (
                                  <div className="text-[10px] text-neutral-400">
                                    Beoordeeld: {new Date(request.reviewed_at).toLocaleDateString('nl-NL')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              );
            })()}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add/Edit Module Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingModule ? 'Module bewerken' : 'Nieuwe module'}</DialogTitle>
            <DialogDescription>
              {editingModule ? 'Pas de module gegevens aan' : 'Voeg een nieuwe premium module toe'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Naam *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="bijv. NIS2 Compliance"
                data-testid="module-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Beschrijving *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Korte beschrijving van de module..."
                rows={3}
                data-testid="module-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Prijs per maand (€)</Label>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  value={formData.price_monthly}
                  onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
                  data-testid="module-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Categorie</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger data-testid="module-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="features">Features (één per regel)</Label>
              <Textarea
                id="features"
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                placeholder="Gap Analysis&#10;Compliance Dashboard&#10;Rapportage tools"
                rows={4}
                data-testid="module-features-input"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800">
              <div>
                <Label className="font-medium">Module actief</Label>
                <p className="text-xs text-neutral-500">Inactieve modules zijn niet zichtbaar voor klanten</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="module-active-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Annuleren
            </Button>
            <Button 
              className="bg-black text-white hover:bg-neutral-800"
              onClick={handleSaveModule}
              disabled={saving}
              data-testid="save-module-button"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingModule ? 'Opslaan' : 'Aanmaken')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminModulesPage;
